import React, { useState, useEffect } from 'react';
import { Crown } from 'lucide-react';

const CheckersBoard = ({ gameState, isMyTurn, onMove, currentUserId }) => {
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [showJumpWarning, setShowJumpWarning] = useState(false);
  
  const board = gameState?.board || [];
  const winner = gameState?.winner;
  const isDraw = gameState?.isDraw;
  const isGameOver = winner || isDraw;
  
  // Determine if current user is player 1 (1, 2) or player 2 (-1, -2)
  const isPlayer1 = gameState?.player1_id === currentUserId;
  const myPieceValues = isPlayer1 ? [1, 2] : [-1, -2];
  
  // Check if any of my pieces have jumps available
  const checkForJumps = (row, col, piece) => {
    const isKing = Math.abs(piece) === 2;
    const directions = [];
    
    if (piece > 0 || isKing) {
      directions.push([1, -1], [1, 1]);
    }
    if (piece < 0 || isKing) {
      directions.push([-1, -1], [-1, 1]);
    }
    
    for (const [dr, dc] of directions) {
      const jumpRow = row + dr * 2;
      const jumpCol = col + dc * 2;
      const midRow = row + dr;
      const midCol = col + dc;
      
      if (jumpRow >= 0 && jumpRow < 8 && jumpCol >= 0 && jumpCol < 8) {
        const targetPiece = board[jumpRow][jumpCol];
        const middlePiece = board[midRow][midCol];
        
        if (targetPiece === 0 && middlePiece !== 0 && Math.sign(middlePiece) !== Math.sign(piece)) {
          return true;
        }
      }
    }
    return false;
  };
  
  const hasJumpsAvailable = () => {
    if (!isMyTurn || !board.length) return false;
    
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (myPieceValues.includes(piece)) {
          if (checkForJumps(row, col, piece)) {
            return true;
          }
        }
      }
    }
    return false;
  };
  
  const mustJump = hasJumpsAvailable();
  
  // Auto-dismiss jump warning after 2 seconds
  useEffect(() => {
    if (showJumpWarning) {
      const timer = setTimeout(() => setShowJumpWarning(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showJumpWarning]);
  
  useEffect(() => {
    if (selectedPiece) {
      // Calculate valid moves for selected piece
      const moves = getValidMovesForPiece(selectedPiece.row, selectedPiece.col);
      setValidMoves(moves);
    } else {
      setValidMoves([]);
    }
  }, [selectedPiece, board, mustJump]);
  
  const getValidMovesForPiece = (row, col) => {
    const moves = [];
    const piece = board[row][col];
    
    if (piece === 0) return moves;
    
    const isKing = Math.abs(piece) === 2;
    const directions = [];
    
    // Regular piece moves in one direction, king moves in all directions
    if (piece > 0 || isKing) {
      // Player 1 or king moving down
      directions.push([1, -1], [1, 1]);
    }
    if (piece < 0 || isKing) {
      // Player 2 or king moving up
      directions.push([-1, -1], [-1, 1]);
    }
    
    // Check for jumps first (mandatory if available)
    const jumps = [];
    for (const [dr, dc] of directions) {
      const jumpRow = row + dr * 2;
      const jumpCol = col + dc * 2;
      const midRow = row + dr;
      const midCol = col + dc;
      
      if (jumpRow >= 0 && jumpRow < 8 && jumpCol >= 0 && jumpCol < 8) {
        const targetPiece = board[jumpRow][jumpCol];
        const middlePiece = board[midRow][midCol];
        
        // Can jump if target is empty and middle has opponent piece
        if (targetPiece === 0 && middlePiece !== 0 && Math.sign(middlePiece) !== Math.sign(piece)) {
          jumps.push({ row: jumpRow, col: jumpCol, isJump: true });
        }
      }
    }
    
    // If there are jumps available anywhere on the board, must take a jump
    if (mustJump || jumps.length > 0) {
      return jumps;
    }
    
    // Regular moves (only if no jumps available)
    for (const [dr, dc] of directions) {
      const newRow = row + dr;
      const newCol = col + dc;
      
      if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
        if (board[newRow][newCol] === 0) {
          moves.push({ row: newRow, col: newCol, isJump: false });
        }
      }
    }
    
    return moves;
  };
  
  const handleSquareClick = (row, col) => {
    if (isGameOver || !isMyTurn) return;
    
    const piece = board[row][col];
    
    // If clicking on own piece, select it
    if (myPieceValues.includes(piece)) {
      const moves = getValidMovesForPiece(row, col);
      
      // If selecting a piece that has no jumps when jumps are required
      if (mustJump && moves.length === 0) {
        setShowJumpWarning(true);
        return;
      }
      
      setSelectedPiece({ row, col });
      return;
    }
    
    // If clicking on valid move destination
    if (selectedPiece) {
      const move = validMoves.find(m => m.row === row && m.col === col);
      if (move) {
        onMove({
          fromRow: selectedPiece.row,
          fromCol: selectedPiece.col,
          toRow: row,
          toCol: col
        });
        setSelectedPiece(null);
        setShowJumpWarning(false);
      }
    }
  };
  
  const renderPiece = (piece) => {
    if (piece === 0) return null;
    
    const isKing = Math.abs(piece) === 2;
    const isPlayer1Piece = piece > 0;
    
    return (
      <div className={`
        w-10 h-10 rounded-full flex items-center justify-center
        ${isPlayer1Piece ? 'bg-red-500 border-red-700' : 'bg-slate-900 border-slate-700'}
        border-4 shadow-lg transition-transform hover:scale-110
      `}>
        {isKing && (
          <Crown className="text-yellow-300" size={20} />
        )}
      </div>
    );
  };
  
  const isValidMoveSquare = (row, col) => {
    return validMoves.some(m => m.row === row && m.col === col);
  };
  
  const isSelected = (row, col) => {
    return selectedPiece?.row === row && selectedPiece?.col === col;
  };
  
  return (
    <div className="p-3 sm:p-4 md:p-6 flex flex-col items-center gap-3 sm:gap-4 md:gap-6">
      {/* Status */}
      <div className="text-center">
        {isGameOver ? (
          <div className="space-y-2">
            {winner ? (
              <>
                <p className="text-2xl font-bold text-emerald-400">
                  {winner === currentUserId ? ' You Won!' : ' You Lost'}
                </p>
                <p className="text-sm text-white/50">
                  {winner === currentUserId ? 'Captured all opponent pieces!' : 'All your pieces were captured'}
                </p>
              </>
            ) : isDraw ? (
              <p className="text-xl text-yellow-400">It's a Draw!</p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-1">
            <div className={`text-lg font-semibold ${isMyTurn ? 'text-emerald-400' : 'text-white/50'}`}>
              {isMyTurn ? 'Your Turn' : 'Opponent\'s Turn'}
            </div>
            {mustJump && isMyTurn && (
              <p className={`text-sm font-semibold ${
                showJumpWarning 
                  ? 'text-red-500 animate-pulse' 
                  : 'text-yellow-400'
              }`}>
                 You must jump!
              </p>
            )}
            {selectedPiece && (
              <p className="text-xs text-white/40">Click a highlighted square to move</p>
            )}
          </div>
        )}
      </div>
      
      {/* Board */}
      <div className="relative">
        {/* 8x8 Grid */}
        <div className="grid grid-cols-8 gap-0 border-4 border-white/20 rounded-lg overflow-hidden shadow-2xl">
          {board.map((row, rowIndex) => (
            row.map((piece, colIndex) => {
              const isLight = (rowIndex + colIndex) % 2 === 0;
              const isValidMove = isValidMoveSquare(rowIndex, colIndex);
              const selected = isSelected(rowIndex, colIndex);
              const canSelect = myPieceValues.includes(piece) && isMyTurn && !isGameOver;
              
              return (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  onClick={() => handleSquareClick(rowIndex, colIndex)}
                  disabled={isGameOver || !isMyTurn}
                  className={`
                    w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 flex items-center justify-center relative
                    transition-all duration-200
                    ${isLight ? 'bg-amber-100' : 'bg-amber-800'}
                    ${selected ? 'ring-4 ring-emerald-400 ring-inset' : ''}
                    ${isValidMove ? 'ring-4 ring-yellow-400 ring-inset animate-pulse' : ''}
                    ${canSelect ? 'hover:brightness-110 cursor-pointer' : ''}
                    ${!isLight && !canSelect && !isValidMove ? 'cursor-default' : ''}
                  `}
                >
                  {renderPiece(piece)}
                  
                  {/* Valid move indicator */}
                  {isValidMove && (
                    <div className="absolute inset-0 bg-yellow-400/20 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-yellow-400/60" />
                    </div>
                  )}
                </button>
              );
            })
          ))}
        </div>
        
        {/* Row/Column Labels */}
        <div className="absolute -left-6 top-0 h-full flex flex-col justify-around text-white/30 text-xs font-mono">
          {[8, 7, 6, 5, 4, 3, 2, 1].map(num => (
            <div key={num}>{num}</div>
          ))}
        </div>
        <div className="absolute -bottom-6 left-0 w-full flex justify-around text-white/30 text-xs font-mono">
          {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(letter => (
            <div key={letter}>{letter}</div>
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-red-500 border-2 border-red-700" />
          <span className="text-white/70">Player 1</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-slate-900 border-2 border-slate-700" />
          <span className="text-white/70">Player 2</span>
        </div>
        <div className="flex items-center gap-2">
          <Crown className="text-yellow-300" size={18} />
          <span className="text-white/70">King</span>
        </div>
      </div>
    </div>
  );
};

export default CheckersBoard;
